import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { fmt } from "@/lib/dates";
import { PageHeader, Panel, Loader, Empty } from "@/components/common";

const STATUS = {
  open: ["Aangeboden", "bg-amber-50 text-amber-800"],
  claimed: ["Wacht op manager", "bg-sky-50 text-sky-800"],
  approved: ["Goedgekeurd", "bg-emerald-50 text-emerald-800"],
  rejected: ["Afgewezen", "bg-red-50 text-red-700"],
  cancelled: ["Ingetrokken", "bg-slate-100 text-slate-500"],
  expired: ["Verlopen", "bg-slate-100 text-slate-500"],
};

const SwapCard = ({ s, act }) => (
  <Panel className="p-4" data-testid={`swap-card-${s.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold capitalize">{fmt(s.date, "EEEE d MMM")} · <span className="font-mono-num">{s.start}–{s.end}</span></p>
        <p className="text-sm text-slate-600 mt-0.5 flex flex-wrap items-center gap-1.5">
          {s.from_name} <ArrowRight className="h-3.5 w-3.5" /> {s.to_name ? `overgenomen door ${s.to_name}` : s.target_name ? `aangeboden aan ${s.target_name}` : "aangeboden aan hele team"}
          {s.position && <span className="text-slate-400">· {s.position}</span>}
        </p>
        {s.note && <p className="text-xs text-slate-500 mt-1">“{s.note}”</p>}
      </div>
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[s.status][1]}`} data-testid={`swap-status-${s.id}`}>{STATUS[s.status][0]}</span>
    </div>
    {(s.can_claim || s.can_cancel || s.can_decide) && (
      <div className="flex flex-wrap gap-2 mt-3">
        {s.can_claim && <Button size="sm" className="rounded-full" onClick={() => act(s.id, "claim", "Je hebt de dienst overgenomen. Wacht op goedkeuring.")} data-testid={`swap-claim-${s.id}`}>Ik neem hem over</Button>}
        {s.can_decide && <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => act(s.id, "approve", "Ruil goedgekeurd, rooster aangepast")} data-testid={`swap-approve-${s.id}`}><Check className="h-4 w-4 mr-1" />Goedkeuren</Button>}
        {s.can_decide && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => act(s.id, "reject", "Ruil afgewezen")} data-testid={`swap-reject-${s.id}`}><X className="h-4 w-4 mr-1" />Afwijzen</Button>}
        {s.can_cancel && !s.can_decide && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => act(s.id, "cancel", "Verzoek ingetrokken")} data-testid={`swap-cancel-${s.id}`}>Intrekken</Button>}
      </div>
    )}
  </Panel>
);

const Section = ({ title, items, act, testid }) => items.length > 0 && (
  <section className="mt-6" data-testid={testid}>
    <h2 className="font-semibold text-lg mb-3">{title} <span className="text-slate-400 font-normal">({items.length})</span></h2>
    <div className="space-y-2">{items.map((s) => <SwapCard key={s.id} s={s} act={act} />)}</div>
  </section>
);

export default function Swaps() {
  const api = useApi();
  const { member, isManager } = useMember();
  const [list, setList] = useState(null);

  const load = useCallback(() => api.get("/swaps").then((r) => setList(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action, ok) => {
    try {
      await api.post(`/swaps/${id}/${action}`);
      toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };

  if (!list) return <Loader />;
  const active = (s) => s.status === "open" || s.status === "claimed";
  const toDecide = list.filter((s) => s.can_decide);
  const offers = list.filter((s) => s.can_claim);
  const mine = list.filter((s) => active(s) && !s.can_decide && !s.can_claim && (s.from_user_id === member.user_id || s.to_user_id === member.user_id || isManager));
  const history = list.filter((s) => !active(s)).slice(0, 20);

  return (
    <div data-testid="swaps-page">
      <PageHeader title="Diensten ruilen" subtitle="Bied een dienst aan via de planning: tik op je eigen dienst.">
        <Button asChild variant="outline" className="rounded-full" data-testid="swaps-to-planning-button"><Link to="/planning">Naar planning</Link></Button>
      </PageHeader>
      {list.length === 0 ? (
        <Empty icon={ArrowLeftRight} title="Nog geen ruilverzoeken" text="Kun je niet werken? Open de planning, tik op je dienst en kies 'Aanbieden'." />
      ) : (
        <>
          <Section title="Wacht op jouw goedkeuring" items={toDecide} act={act} testid="swaps-to-decide" />
          <Section title="Beschikbaar om over te nemen" items={offers} act={act} testid="swaps-offers" />
          <Section title="Lopende verzoeken" items={mine} act={act} testid="swaps-mine" />
          <Section title="Afgerond" items={history} act={act} testid="swaps-history" />
        </>
      )}
    </div>
  );
}
